import {
  SizeColumnsToContentStrategy,
  SizeColumnsToFitGridStrategy,
  SizeColumnsToFitProvidedWidthStrategy,
} from "ag-grid-enterprise";
import { AgGridReact } from "ag-grid-react";
import axios, { AxiosResponse } from "axios";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";

import useFetch from "../../../hooks/useFetchMSAL";
import { TimePhasedSafetyStockFetchAPI } from "../../../models/APIResponses.model";
import CellEditTrack from "../../../models/CellEditTrack.model";
import TimePhasedSafetyStockModel from "../../../models/PlanningParameters/TimePhasedSafetyStock.model";
import {
  AG_GRID_DEFAULT_COL_DEF,
  AG_GRID_MODULES,
  AG_GRID_OPTIONS,
  TIME_PHASED_SAFETY_STOCK_TAB_ID,
  TOAST_CONTAINER_ID,
} from "../../../shared/constants";
import { getToastOptions, newGridRowStyleHandler, removeInsertedRowsById } from "../../../shared/functions";
import { RootState } from "../../../store";
import { planningParametersTabActions } from "../../../store/slices/planning-parameters-tab-slice";
import "../../../styles.css";
import EditableGridActions from "../../UI/EditableGridActions/EditableGridActions";

const TimePhasedSafetyStockTable = (props: {
  gridHeight: string;
}) => {
  const [, fetchData] = useFetch([]);

  // States
  const [agRowData, setAgRowData] = useState<TimePhasedSafetyStockModel[]>([]);
  const [agRowDataCopy, setAgRowDataCopy] = useState<
    TimePhasedSafetyStockModel[]
  >([]);
  const [cellEditTracks, setCellEditTracks] = useState<{
    [key: string]: { [key: string]: CellEditTrack };
  }>({});
  const [changeTrackState, setChangeTrackState] = useState(false);
  const [insertedRows, setInsertedRows] = useState<any>({});
  const [selectedRowIds, setSelectedRowIds] = useState<any[]>([]);
  const [newRowCount, setNewRowCount] = useState<number>(1);
  const [enableDelButton, setEnableDelButton] = useState(false);

  // Variables
  const agGridRef = useRef<AgGridReact>(null);
  const [safetyStockPolicyValues, setSafetyStockPolicyValues] = useState<
    string[]
  >([]);
  const autoSizeStrategy = useMemo<
    | SizeColumnsToFitGridStrategy
    | SizeColumnsToFitProvidedWidthStrategy
    | SizeColumnsToContentStrategy
  >(() => {
    return {
      type: "fitGridWidth",
      defaultMinWidth: 100,
      columnLimits: [
        {
          colId: "country",
          minWidth: 900,
        },
      ],
    };
  }, []);

  const defaultColDef = useMemo(() => {
    return {
      editable: true,
      ...(AG_GRID_DEFAULT_COL_DEF as any),
    };
  }, []);

  // Redux variables
  const dispatchFn = useDispatch();
  const activeLowerSubTabId: string = useSelector((state: RootState) => state.planningParametersTab.activeSubTabId.lower);
  const tabApiDataFetched: boolean = useSelector((state: RootState) => state.planningParametersTab.tabApiDataFetched)[TIME_PHASED_SAFETY_STOCK_TAB_ID];
  const selectedGridSlicers = useSelector(
    (state: RootState) => state.planningParametersTab.selectedGridSlicers
  );
  const isTimePhasedSafetyStockUpdated = useSelector(
    (state: RootState) =>
      state.planningParametersTab.isTimePhasedSafetyStockUpdated
  );

  const isWeekendDate = (date: Date): boolean => {
    const dayOfWeek = new Date(date).getDay();
    return dayOfWeek === 0 || dayOfWeek === 6;
  }

  const isFutureDate = (date: Date): boolean => {
    const currentDate = new Date();
    const selectedDate = new Date(date);
    return selectedDate > currentDate;
  }

  const ColDef: any[] = [
    {
      headerName: "Safety Stock Policy",
      field: "safety_stock_policy",
      cellDataType: "text",
      cellEditor: "agSelectCellEditor",
      cellEditorParams: { values: safetyStockPolicyValues },
      headerCheckboxSelection: true,
      checkboxSelection: true,
      showDisabledCheckboxes: true,
      suppressHeaderMenuButton: true,
      sortable: false,
    },
    {
      headerName: "Week End Date Start",
      field: "week_end_date_start",
      suppressHeaderMenuButton: true,
      sortable: false,
      valueGetter: (params: any) => {
        return params.data.week_end_date_start;
      },
      valueSetter: (params: any) => {
        if (isWeekendDate(params.newValue) && isFutureDate(params.newValue)) {
          params.data.week_end_date_start = params.newValue;
          return true;
        } else {
          toast.error("Please select a future week end date.");
          return false;
        }
      },
      cellEditor: "agDateStringCellEditor",
    },
    {
      headerName: "Range of Weeks of Demand",
      field: "range_of_weeks_of_demand",
      cellDataType: "number",
      cellClass: "ag-right-aligned-cell",
      headerClass: "ag-left-aligned-cell",
      cellEditor: "agNumberCellEditor",
      suppressHeaderMenuButton: true,
      sortable: false,
    },
    {
      headerName: "Fraction of Demand",
      field: "fraction_of_demand",
      cellDataType: "number",
      cellClass: "ag-right-aligned-cell",
      headerClass: "ag-left-aligned-cell",
      cellEditor: "agNumberCellEditor",
      suppressHeaderMenuButton: true,
      sortable: false,
    },
  ];

  /**
   * Fetches time-phased safety stock policy data from API
   * @param {any} body Request body
   */
  const fetchTimePhasedSafetyStock = useCallback(
    async (body: any): Promise<void> => {
      setChangeTrackState(false);
      const fetchingDataToastID = toast.loading("Fetching Time-phased Safety Stock Policy data...", {
        containerId: TOAST_CONTAINER_ID,
        ...getToastOptions("loading"),
      });
      try {
        const tpssFetchAPIResponse: AxiosResponse<TimePhasedSafetyStockFetchAPI> =
          await fetchData(
            `/get-time-phased-safety-stock`,
            {
              method: 'POST',
              data: {}
            }
          );

        toast.dismiss({
          id: fetchingDataToastID,
          containerId: TOAST_CONTAINER_ID,
        });

        if (tpssFetchAPIResponse.data.data) {
          setAgRowData(tpssFetchAPIResponse.data.data);
          setAgRowDataCopy(
            JSON.parse(JSON.stringify(tpssFetchAPIResponse.data.data))
          );
          setSafetyStockPolicyValues(
            tpssFetchAPIResponse.data.cell_dropdowns.safety_stock_policy
          );
          dispatchFn(planningParametersTabActions.setTabApiDataFetched({
            [TIME_PHASED_SAFETY_STOCK_TAB_ID]: true
          }));

        } else {
          toast.error("Error in fetching Time-phased Safety Stock Policy data", {
            containerId: TOAST_CONTAINER_ID,
            ...getToastOptions("error"),
          });
        }
      } catch (error: Error | any) {
        console.error(`Request Error: ${error}`);
        toast.dismiss({
          id: fetchingDataToastID,
          containerId: TOAST_CONTAINER_ID,
        });
        toast.error("Error in fetching Time-phased Safety Stock Policy data", {
          containerId: TOAST_CONTAINER_ID,
          ...getToastOptions("error"),
        });
      }
    },
    [dispatchFn, fetchData]
  );

  /**
   * Event handler for when cell editing is stopped
   * @param {any} params Cell editing stop event parameters
   */
  const onCellEditingStoppedHandler = (params: any) => {
    try {
      const id = params.node.data.row_id;
      const colName = params.column.colId;

      if (id.toString().startsWith("new_")) {
        setInsertedRows((prev: any) => {
          let newData = prev;
          let newRow = newData[id] ? newData[id] : {};

          newRow[colName] = params.newValue;
          newData[id] = newRow;
          return newData;
        });
      } else {
        if (
          params.oldValue !== params.newValue &&
          params.newValue !== undefined
        ) {
          let currentRowNode = agGridRef.current!.api!.getRowNode(
            params.node.id
          );

          setCellEditTracks((prev) => {
            let newTrackEdits = prev;
            let cellEditTrack = newTrackEdits[id] ? newTrackEdits[id] : {};

            cellEditTrack[colName] = params.newValue;

            newTrackEdits[id] = cellEditTrack;
            return newTrackEdits;
          });
          dispatchFn(planningParametersTabActions.setIsPlanningParametersTabEdited({ key: 'timePhasedSafetyStock', value: true }));

          setChangeTrackState(true);
          agGridRef.current!.api!.applyTransaction({
            update: [currentRowNode?.data],
          });
        } else if (params.newValue === params.oldValue) {
          dispatchFn(planningParametersTabActions.setIsPlanningParametersTabEdited({ key: 'timePhasedSafetyStock', value: false }));

        }
      }
    } catch (error: any) {
      console.error(`Error: ${error}`);
    }
  };

  /**
   * Sets selected rows on selection checkbox change
   */
  const onSelectionChangedHandler = () => {
    const selectedRows = agGridRef.current!.api!.getSelectedRows();
    const selectedRowIds: any[] = selectedRows.map((el) => el.row_id);
    setSelectedRowIds(selectedRowIds);
  };

  /**
   * Deletes selected rows
   * @param {any[]} selectedRowIds Selected Row IDs
   */
  const deleteSelectedRowData = async (selectedRowIds: any[]) => {
    let deleteRowsToastID;

    try {
      // Delete selected newly inserted rows
      const oldDataRowIds: any[] = selectedRowIds.filter(rowId => !rowId.toString().startsWith("new_"));
      setInsertedRows((insertedRows: any) => removeInsertedRowsById(insertedRows, selectedRowIds));

      if (oldDataRowIds.length) {
        deleteRowsToastID = toast.loading("Deleting Row...", {
          containerId: TOAST_CONTAINER_ID,
          ...getToastOptions("loading"),
        });

        const deleteReqResponse: AxiosResponse =
          await axios.delete(`/delete-time-phased-safety-stock`,
            {
              method: 'DELETE',
              data: { row_ids_to_delete: selectedRowIds },
            }
          );

        toast.dismiss({
          id: deleteRowsToastID,
          containerId: TOAST_CONTAINER_ID,
        });

        if (deleteReqResponse.data.success) {
          setCellEditTracks({});
          setInsertedRows({});

          toast.success("Data updated successfully", {
            containerId: TOAST_CONTAINER_ID,
            ...getToastOptions("success"),
          });

          dispatchFn(planningParametersTabActions.setTabApiDataFetched({
            [TIME_PHASED_SAFETY_STOCK_TAB_ID]: false
          }));
          dispatchFn(planningParametersTabActions.setIsTimePhasedSafetyStockUpdated(!isTimePhasedSafetyStockUpdated));

        } else if (deleteReqResponse.data.error) {
          toast.error(deleteReqResponse.data.error, {
            containerId: TOAST_CONTAINER_ID,
            ...getToastOptions("error"),
          });
        }
      } else {
        const selectedRows: any[] | undefined = agGridRef.current?.api.getSelectedRows();
        agGridRef.current?.api.applyTransaction({
          remove: selectedRows
        });
      }
    } catch (error: any) {
      console.error(error);
      toast.error("Something went wrong.", {
        containerId: TOAST_CONTAINER_ID,
        ...getToastOptions("error"),
      });
    }
  };

  /**
   * Updates cell values by using part site source update API
   * @param {any} cellEditTracks Cell edits in the grid
   * @param {any} insertedRows Newly inserted rows
   */
  const updateCellValues = async (
    cellEditTracks: any,
    insertedRows: any
  ): Promise<void> => {
    try {
      const updateToastID = toast.loading("Updating Time-phased Safety Stock Policy data...", {
        containerId: TOAST_CONTAINER_ID,
        ...getToastOptions("loading"),
      });

      const updateReqResponse: AxiosResponse = await fetchData(
        `/update-time-phased-safety-stock`,
        {
          method: 'PUT',
          data: {
            updated_data: cellEditTracks,
            inserted_data: insertedRows,
          }
        }
      );

      toast.dismiss({
        id: updateToastID,
        containerId: TOAST_CONTAINER_ID,
      });

      if (updateReqResponse.data.success) {
        setCellEditTracks({});
        setInsertedRows({});

        dispatchFn(planningParametersTabActions.setIsPlanningParametersTabEdited({ key: 'timePhasedSafetyStock', value: false }));
        dispatchFn(planningParametersTabActions.setIsTimePhasedSafetyStockUpdated(!isTimePhasedSafetyStockUpdated));


        toast.success("Time-phased Safety Stock Policy data updated successfully.", {
          containerId: TOAST_CONTAINER_ID,
          ...getToastOptions("success"),
        });

        dispatchFn(planningParametersTabActions.setTabApiDataFetched({
          [TIME_PHASED_SAFETY_STOCK_TAB_ID]: false
        }));

      } else if (updateReqResponse.data.error) {
        toast.error(updateReqResponse.data.error, {
          containerId: TOAST_CONTAINER_ID,
          ...getToastOptions("error"),
        });
      }
    } catch (error: any) {
      console.error(error);
      toast.error("Error in updating the Time-phased Safety Stock Policy data", {
        containerId: TOAST_CONTAINER_ID,
        ...getToastOptions("error"),
      });
    }
  };

  /**
   * Event handler for save button click
   * @param {any} event Save button click event data
   */
  const onUpdateCellValuesHandler = async (event: any): Promise<void> => {
    try {
      const updatedNewRowData: any = {};
      for (let key in insertedRows) {
        if (Object.keys(insertedRows[key]).length) {
          updatedNewRowData[key] = insertedRows[key];
        }
      }

      if (
        Object.keys(cellEditTracks).length ||
        Object.keys(updatedNewRowData).length
      ) {
        updateCellValues(cellEditTracks, updatedNewRowData);
      }
    } catch (error: Error | any) {
      console.error(`Error: ${error}`);
    }
  };

  /**
   * Event handler for reset button click
   * @param {any} params Reset button click event data
   */
  const resetCellEdits = async (params: any): Promise<void> => {
    try {
      const resetConsent: boolean = window.confirm(
        "Do you want to reset the changes done to the grid?"
      );

      if (resetConsent) {
        setCellEditTracks({});
        setChangeTrackState(false);
        setAgRowDataCopy(JSON.parse(JSON.stringify([...agRowData])));
        dispatchFn(planningParametersTabActions.setIsPlanningParametersTabEdited({ key: 'timePhasedSafetyStock', value: false }));
      }
    } catch (error: Error | any) {
      console.error(`Error: ${error}`);
    }
  };

  const gridOptions = {
    ...(AG_GRID_OPTIONS as any),
    onSelectionChanged: onSelectionChangedHandler,
    getRowStyle: (params: any) => newGridRowStyleHandler(params, "row_id")
  };

  /**
   * Inserts a new row into the grid
   */
  const addRowToGrid = (): void => {
    const newRowId: string = `new_${newRowCount.toString()}`;
    agGridRef.current!.api!.applyTransaction({
      add: [{ row_id: newRowId }],
      addIndex: 0,
    });
    setInsertedRows((insertedRows: any) => {
      return {
        ...insertedRows,
        [newRowId]: {}
      };
    });
    dispatchFn(planningParametersTabActions.setIsPlanningParametersTabEdited({ key: 'timePhasedSafetyStock', value: true }));
    setChangeTrackState(true);
    setNewRowCount(newRowCount + 1);
  };

  /**
   * Deletes selected rows from the grid
   */
  const deleteSelectedRowsFromGrid = (): void => {
    deleteSelectedRowData(selectedRowIds);
    setSelectedRowIds([]);
    setEnableDelButton(false);
  };

  // Initial data fetch
  useEffect(() => {
    if (
      !tabApiDataFetched &&
      activeLowerSubTabId === TIME_PHASED_SAFETY_STOCK_TAB_ID
    ) {
      fetchTimePhasedSafetyStock(selectedGridSlicers);
    }
  }, [tabApiDataFetched, activeLowerSubTabId, fetchTimePhasedSafetyStock, selectedGridSlicers]);

  useEffect(() => {
    if (selectedRowIds.length > 0) {
      setEnableDelButton(true);
    } else {
      setEnableDelButton(false);
    }
  }, [selectedRowIds]);

  useEffect(() => {
    if (
      Object.keys(insertedRows).length === 0 &&
      Object.keys(cellEditTracks).length === 0
    ) {
      setChangeTrackState(false);
    }
  }, [insertedRows, cellEditTracks]);

  return (
    <>
      <div className="row mb-2">
        <div className="col offset-8 col-4 text-end">
          <EditableGridActions
            tabId={TIME_PHASED_SAFETY_STOCK_TAB_ID}
            disableIf={{
              resetBtn: !changeTrackState,
              saveBtn: !changeTrackState,
              deleteRowBtn: !enableDelButton
            }}
            onClick={{
              resetBtn: resetCellEdits,
              saveBtn: onUpdateCellValuesHandler,
              addRowBtn: addRowToGrid,
              deleteRowBtn: deleteSelectedRowsFromGrid
            }}
          />
        </div>
      </div>

      <div
        className="ag-theme-balham mt-2"
        style={{
          height: props.gridHeight,
          maxHeight: props.gridHeight,
          width: "100%",
          overflowX: "auto",
          overflowY: "auto",
        }}
      >
        <AgGridReact
          ref={agGridRef}
          rowData={agRowDataCopy}
          columnDefs={ColDef}
          defaultColDef={defaultColDef}
          autoSizeStrategy={autoSizeStrategy}
          onCellEditingStopped={onCellEditingStoppedHandler}
          gridOptions={gridOptions}
          rowSelection={"multiple"}
          modules={AG_GRID_MODULES}
        // suppressAggFuncInHeader={true}
        />
      </div>
    </>
  );
};

export default TimePhasedSafetyStockTable;
